import type { WidgetRatingResponse } from "./lifeBetter";
import {
  getStoredToken,
  getTokenFromUrl,
  storeToken,
  storeEmail,
  getDiscountCode,
} from "./emailStorage";

const API_BASE =
  import.meta.env.VITE_PUBLIC_SOCIAL_OCEAN_BASE_URL || "https://socialocean.co";
const PROXY_PREFIX = "/api/widget/buy-again";
const STORED_SHOPIFY_CUSTOMER_ID_KEY = "buy-again-shopify-customer-id";

function getWindowCustomerId(): number | null {
  if (typeof window === "undefined") return null;
  const id = (
    window as unknown as { meta?: { page?: { customerId?: number } } }
  )?.meta?.page?.customerId;
  return id != null && Number.isFinite(id) ? id : null;
}

function getStoredShopifyCustomerId(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORED_SHOPIFY_CUSTOMER_ID_KEY);
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function persistShopifyCustomerIdFromWindow(id: number): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORED_SHOPIFY_CUSTOMER_ID_KEY, String(id));
  } catch {
    /* ignore quota / private mode */
  }
}

export function getCustomerIdOrThrow(): number {
  const winId = getWindowCustomerId();
  if (winId != null) {
    persistShopifyCustomerIdFromWindow(winId);
    return winId;
  }
  const storedId = getStoredShopifyCustomerId();
  if (storedId != null) {
    return storedId;
  }
  throw new Error(
    "Customer ID not available (window.meta.page.customerId or stored id)",
  );
}

export function hasCustomerId(): boolean {
  return (
    getWindowCustomerId() != null ||
    getStoredShopifyCustomerId() != null ||
    getTokenFromUrl() != null ||
    getStoredToken() != null
  );
}

export type CustomerIdentifier =
  | { type: "id"; shopify_customer_id: number }
  | { type: "token"; buy_again_token: string };

export function getCustomerIdentifier(
  skipUrlToken = false,
): CustomerIdentifier | null {
  const winId = getWindowCustomerId();
  if (winId != null) {
    persistShopifyCustomerIdFromWindow(winId);
    return { type: "id", shopify_customer_id: winId };
  }
  if (!skipUrlToken) {
    const urlToken = getTokenFromUrl();
    if (urlToken) return { type: "token", buy_again_token: urlToken };
  }
  const token = getStoredToken();
  if (token) return { type: "token", buy_again_token: token };
  const storedId = getStoredShopifyCustomerId();
  if (storedId != null) return { type: "id", shopify_customer_id: storedId };
  return null;
}

async function backendFetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const base = API_BASE.replace(/\/$/, "");
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options?.headers as Record<string, string>),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      (data as { message?: string })?.message ||
      (data as { error?: { message?: string } })?.error?.message ||
      (typeof (data as { error?: unknown }).error === "string"
        ? (data as { error: string }).error
        : undefined) ||
      `Request failed: ${path}`;
    const err = new Error(msg) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return (data?.data !== undefined ? data.data : data) as T;
}

async function backendPostEmptyResponse(
  path: string,
  body: Record<string, unknown>,
): Promise<void> {
  const base = API_BASE.replace(/\/$/, "");
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const raw = await res.text();
  let data: Record<string, unknown> = {};
  if (raw.trim()) {
    try {
      data = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      data = {};
    }
  }
  if (!res.ok) {
    const msg =
      (data.message as string | undefined) ||
      (typeof data.error === "string" ? data.error : undefined) ||
      (data.error as { message?: string } | undefined)?.message ||
      `Request failed: ${path}`;
    const err = new Error(msg) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
}

/** POST JSON; returns full parsed body (no `data` unwrap). */
async function backendPostJsonFull(
  path: string,
  body: Record<string, unknown>,
): Promise<unknown> {
  const base = API_BASE.replace(/\/$/, "");
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const raw = await res.text();
  let parsed: unknown = null;
  if (raw.trim()) {
    try {
      parsed = JSON.parse(raw) as unknown;
    } catch {
      parsed = null;
    }
  }
  const errPayload =
    parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : {};
  if (!res.ok) {
    const msg =
      (typeof errPayload.message === "string"
        ? errPayload.message
        : undefined) ||
      (typeof errPayload.error === "string" ? errPayload.error : undefined) ||
      (errPayload.error &&
      typeof errPayload.error === "object" &&
      errPayload.error !== null &&
      typeof (errPayload.error as { message?: string }).message === "string"
        ? (errPayload.error as { message: string }).message
        : undefined) ||
      `Request failed: ${path}`;
    const err = new Error(msg) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return parsed;
}

function extractHttpUrlFromStayAiPayload(payload: unknown): string | null {
  const isUrl = (v: unknown): string | null => {
    if (typeof v !== "string") return null;
    const t = v.trim();
    return /^https?:\/\//i.test(t) ? t : null;
  };

  if (payload == null) return null;
  if (typeof payload === "string") return isUrl(payload);

  if (typeof payload !== "object") return null;
  const o = payload as Record<string, unknown>;
  const nested =
    o.data && typeof o.data === "object" && o.data !== null
      ? (o.data as Record<string, unknown>)
      : null;

  for (const v of [
    o.url,
    o.checkout_url,
    o.checkoutUrl,
    o.redirect_url,
    o.redirectUrl,
    o.order_url,
    o.orderUrl,
    nested?.url,
    nested?.checkout_url,
    nested?.checkoutUrl,
    nested?.redirect_url,
  ]) {
    const u = isUrl(v);
    if (u) return u;
  }
  return null;
}

function buildQueryParams(
  identifier: CustomerIdentifier,
  discountCode?: string | null,
): string {
  let params: string;
  if (identifier.type === "id") {
    params = `shopify_customer_id=${encodeURIComponent(identifier.shopify_customer_id)}`;
  } else {
    params = `buy_again_token=${encodeURIComponent(identifier.buy_again_token)}`;
  }
  if (discountCode) {
    params += `&discount_code=${encodeURIComponent(discountCode)}`;
  }
  return params;
}

export interface PurchasedProductsResponse {
  customer?: {
    shopify_customer_id?: string | number;
    email?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
  };
  products?: unknown[];
}

export interface PurchasedProductsResult {
  products: unknown[];
  customer?: PurchasedProductsResponse["customer"];
}

function normalizePurchasedProductsResult(
  result: unknown[] | PurchasedProductsResponse,
): PurchasedProductsResult {
  if (Array.isArray(result)) {
    return { products: result, customer: undefined };
  }
  if (result && typeof result === "object" && Array.isArray(result.products)) {
    return {
      products: result.products,
      customer: result.customer,
    };
  }
  return { products: [], customer: undefined };
}

const NO_IDENTIFIER_MSG =
  "Customer identifier not available (customer ID or token from storage)";

async function fetchWithTokenFallback<T>(
  pathForIdentifier: (id: CustomerIdentifier) => string,
  parse: (data: unknown) => T,
): Promise<T> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);

  const urlToken = getTokenFromUrl();
  const usedUrlToken =
    identifier.type === "token" && urlToken === identifier.buy_again_token;

  try {
    const result = await backendFetch<unknown>(pathForIdentifier(identifier), {
      cache: "no-store",
    });
    if (usedUrlToken) {
      storeToken(identifier.buy_again_token);
    }
    return parse(result);
  } catch {
    if (usedUrlToken && getStoredToken()) {
      const fallback = getCustomerIdentifier(true);
      if (fallback) {
        const result = await backendFetch<unknown>(
          pathForIdentifier(fallback),
          { cache: "no-store" },
        );
        return parse(result);
      }
    }
    throw new Error(NO_IDENTIFIER_MSG);
  }
}

export async function fetchPurchasedProducts(): Promise<PurchasedProductsResult> {
  const discountCode = getDiscountCode();
  return fetchWithTokenFallback<PurchasedProductsResult>(
    (id) =>
      `${PROXY_PREFIX}/purchased-products?${buildQueryParams(id, discountCode)}`,
    (r) =>
      normalizePurchasedProductsResult(
        r as unknown[] | PurchasedProductsResponse,
      ),
  );
}

export async function fetchPurchasedProductDetail(
  productId: number,
): Promise<unknown | null> {
  const discountCode = getDiscountCode();
  return fetchWithTokenFallback<unknown | null>(
    (id) =>
      `${PROXY_PREFIX}/purchased-products/${productId}?${buildQueryParams(id, discountCode)}`,
    (r) => (r as unknown) ?? null,
  );
}

export async function pauseStayAiSubscription(
  subscriptionId: number,
  pausedUntil: string,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/pause?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, { paused_until: pausedUntil });
}

export async function extendPauseStayAiSubscription(
  subscriptionId: number,
  pausedUntil: string,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/extend-pause?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, { paused_until: pausedUntil });
}

export async function unpauseStayAiSubscription(
  subscriptionId: number,
  nextBillingDate: string,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/unpause?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, {
    next_billing_date: nextBillingDate,
  });
}

/** Cancel-chat provenance for retention actions taken mid-conversation, so
 * app-backend can stamp the conversation as `saved`. Only sent when the
 * action was actually taken from within the chat — the sheet's own
 * (non-chat) delay/change-frequency controls omit it. */
export interface CancelChatOfferContext {
  conversationId?: string | null;
  offerTaken?: string | null;
}

export async function setNextBillingDateStayAiSubscription(
  subscriptionId: number,
  nextBillingDate: string,
  opts?: CancelChatOfferContext,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/set-next-billing-date?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, {
    next_billing_date: nextBillingDate,
    ...(opts?.conversationId
      ? {
        conversation_id: opts.conversationId,
        offer_taken: opts.offerTaken ?? null,
      }
      : {}),
  });
}

export type StayAiIntervalUnit = "day" | "week" | "month" | "year";

export async function changeFrequencyStayAiSubscription(
  subscriptionId: number,
  interval: number,
  intervalUnit: StayAiIntervalUnit,
  opts?: CancelChatOfferContext,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/change-frequency?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, {
    interval,
    interval_unit: intervalUnit,
    ...(opts?.conversationId
      ? {
        conversation_id: opts.conversationId,
        offer_taken: opts.offerTaken ?? null,
      }
      : {}),
  });
}

/**
 * Apply the cancel-chat size-up bundle: swap the (single) line to a bigger variant
 * AND re-cadence the contract to the longer interval, in one backend call. The
 * backend runs the two Stay AI writes (swap-product then change-frequency) and
 * updates the local DB, mirroring the synchronous change-frequency path. Needs
 * only Shopify GIDs — no Stay AI selling-plan id. See the backend
 * `applySizeUpBundle` controller.
 *
 * Pricing:
 * - `price` — exact Stay AI charge (`isDiscounted: true` = use as-is). Include
 *   any STAY15-style promo (promo-folded net).
 * - `unitPrice` — Subscribe & Save catalog amount (after selling plan, before
 *   promo). Stored locally as `unit_price`; `line_discounted_price` gets `price`.
 */
export async function applySizeUpBundleStayAiSubscription(
  subscriptionId: number,
  args: {
    /** Shopify ProductVariant numeric id to swap to (backend formats the GID). */
    variantId: number;
    /** Exact per-delivery charge for Stay AI (`isDiscounted: true`). */
    price: number;
    /**
     * Subscribe & Save catalog price (after selling plan, before promo). Stored
     * as local `unit_price`. Defaults to `price` when omitted.
     */
    unitPrice?: number;
    quantity: number;
    interval: number;
    intervalUnit: StayAiIntervalUnit;
    /**
     * The billing/shipment date the customer chose on accept (YYYY-MM-DD). When
     * set, the backend bills the new plan from this date instead of the
     * interval-derived default; omit to keep the computed date.
     */
    nextBillingDate?: string;
  },
  opts?: CancelChatOfferContext,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/size-up-bundle?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, {
    variant_id: args.variantId,
    price: args.price,
    ...(args.unitPrice != null ? { unit_price: args.unitPrice } : {}),
    quantity: args.quantity,
    interval: args.interval,
    interval_unit: args.intervalUnit,
    ...(args.nextBillingDate
      ? { next_billing_date: args.nextBillingDate }
      : {}),
    ...(opts?.conversationId
      ? {
        conversation_id: opts.conversationId,
        offer_taken: opts.offerTaken ?? null,
      }
      : {}),
  });
}

export async function skipNextOrderStayAiSubscription(
  subscriptionId: number,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/skip-next-order?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, {});
}

export async function reactivateStayAiSubscription(
  subscriptionId: number,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/reactivate?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, {});
}

/** Send a free-text support message about a subscription (emailed to the store). */
export async function contactSupportStayAiSubscription(
  subscriptionId: number,
  message: string,
  opts?: {
    productTitle?: string | null;
    customerEmail?: string | null;
    customerName?: string | null;
  },
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/contact-support?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, {
    message,
    ...(opts?.productTitle ? { product_title: opts.productTitle } : {}),
    ...(opts?.customerEmail ? { customer_email: opts.customerEmail } : {}),
    ...(opts?.customerName ? { customer_name: opts.customerName } : {}),
  });
}

/** One subscription's details, as forwarded to support in a merge request. */
export interface SubscriptionMergeRequestSubscription {
  subscription_id: number;
  product_titles: string[];
  next_billing_date?: string | null;
  shipping_address?: string | null;
}

export interface SubscriptionMergeRequestInput {
  subscriptions: SubscriptionMergeRequestSubscription[];
  /** Human-readable shared frequency, e.g. "every 2 weeks". */
  frequency_label: string;
  /** Chosen delivery date for the merged subscription (YYYY-MM-DD). */
  delivery_date: string;
  /** Whether the date came from an existing subscription or was newly picked. */
  delivery_date_source: "existing" | "custom";
  /** True when the selected subscriptions ship to more than one address. */
  differing_addresses: boolean;
}

export interface SubscriptionMergeRequestResult {
  /**
   * True when the backend combined the subscriptions right away; false when
   * the request went to support to complete manually.
   */
  completed: boolean;
}

/**
 * Combine several same-frequency subscriptions into one. Depending on the
 * backend's automation setting this either performs the combine immediately
 * (`completed: true`) or emails the details to the store's support inbox to
 * finish manually (`completed: false`).
 */
export async function requestSubscriptionMergeStayAi(
  input: SubscriptionMergeRequestInput,
  opts?: {
    customerEmail?: string | null;
    customerName?: string | null;
  },
): Promise<SubscriptionMergeRequestResult> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/merge-request?${buildQueryParams(identifier, discountCode)}`;
  const result = await backendFetch<{ completed?: boolean }>(path, {
    method: "POST",
    body: JSON.stringify({
      ...input,
      ...(opts?.customerEmail ? { customer_email: opts.customerEmail } : {}),
      ...(opts?.customerName ? { customer_name: opts.customerName } : {}),
    }),
  });
  return { completed: result?.completed === true };
}

export interface SubscriptionMergeShippingPreview {
  /** Shipping fee of the single merged order, or null when Shopify returns no rate. */
  mergedShippingPrice: number | null;
  currency: string | null;
  rateTitle: string | null;
}

/**
 * Preview the shipping cost of merging the given subscriptions into one order.
 * The backend prices the combined cart via Shopify `draftOrderCalculate` (a pure
 * calculation — nothing is created). Callers derive the saving by subtracting the
 * returned fee from the subscriptions' current shipping fees.
 */
export async function previewSubscriptionMergeShipping(
  subscriptionIds: (number | string)[],
): Promise<SubscriptionMergeShippingPreview> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/merge-preview?${buildQueryParams(identifier, discountCode)}`;
  const full = await backendPostJsonFull(path, {
    subscription_ids: subscriptionIds.map((id) => String(id)),
  });
  const root =
    full && typeof full === "object" ? (full as Record<string, unknown>) : {};
  const data =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : root;
  const price = data.merged_shipping_price;
  return {
    mergedShippingPrice:
      typeof price === "number" && Number.isFinite(price) ? price : null,
    currency: typeof data.currency === "string" ? data.currency : null,
    rateTitle: typeof data.rate_title === "string" ? data.rate_title : null,
  };
}

export interface SubscriptionShippingPreview {
  shippingPrice: number | null;
  currency: string | null;
  rateTitle: string | null;
}

/**
 * Preview shipping for a subscription as if it carried the given variant
 * (cancel-chat size-up). Uses Shopify `draftOrderCalculate` so free-shipping
 * thresholds (e.g. order > $100) are reflected exactly — do not reuse the
 * contract's current deliveryPrice for the hypothetical cart.
 */
export async function previewSubscriptionShipping(
  subscriptionId: number | string,
  args: { variantId: number; quantity?: number },
): Promise<SubscriptionShippingPreview> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/shipping-preview?${buildQueryParams(identifier, discountCode)}`;
  const full = await backendPostJsonFull(path, {
    variant_id: args.variantId,
    quantity: args.quantity ?? 1,
  });
  const root =
    full && typeof full === "object" ? (full as Record<string, unknown>) : {};
  const data =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : root;
  const price = data.shipping_price;
  return {
    shippingPrice:
      typeof price === "number" && Number.isFinite(price) ? price : null,
    currency: typeof data.currency === "string" ? data.currency : null,
    rateTitle: typeof data.rate_title === "string" ? data.rate_title : null,
  };
}

export interface StayAiShippingAddressInput {
  first_name: string;
  last_name: string;
  address1: string;
  address2?: string | null;
  city: string;
  country: string;
  country_code?: string | null;
  province: string;
  province_code: string;
  zip: string;
  phone: string;
}

export async function setShippingAddressStayAiSubscription(
  subscriptionId: number,
  address: StayAiShippingAddressInput,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/set-shipping-address?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, { ...address });
}

export async function getOrderNowStayAiSubscription(
  subscriptionId: number,
): Promise<{
  redirectUrl: string | null;
  message: string | null;
}> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/get-order-now?${buildQueryParams(identifier, discountCode)}`;
  const full = await backendPostJsonFull(path, {});
  const o =
    full && typeof full === "object" && full !== null
      ? (full as Record<string, unknown>)
      : {};
  const message =
    typeof o.message === "string" ? o.message.trim() || null : null;
  const inner = o.data;
  const innerObj =
    inner && typeof inner === "object" && inner !== null
      ? (inner as Record<string, unknown>)
      : null;

  if (o.success === false) {
    throw new Error(message ?? "Request failed.");
  }
  if (innerObj && innerObj.success === false) {
    throw new Error(message ?? "Request failed.");
  }

  const redirectUrl =
    extractHttpUrlFromStayAiPayload(full) ??
    extractHttpUrlFromStayAiPayload(inner);
  return { redirectUrl, message };
}

export interface CancellationReasonPayload {
  whyBought?: string | null;
  /** The specific cancel driver in the customer's own words (chat intake). */
  whyDetail?: string | null;
  cancelReasonId?: string | null;
  cancelReasonLabel?: string | null;
  cancelReasonOther?: string | null;
  productId?: number | null;
  orderCount?: number | null;
  isBeforeSecondShipment?: boolean | null;
  offerDeclined?: boolean | null;
  /** Session id for the cancel chatbot conversation, if the customer went through chat. */
  conversationId?: string | null;
}

export function buildCancellationBody(
  meta: CancellationReasonPayload,
): Record<string, unknown> {
  return {
    cancel_reason_id: meta.cancelReasonId ?? null,
    cancel_reason_label: meta.cancelReasonLabel ?? null,
    cancel_reason_other: meta.cancelReasonOther ?? null,
    why_bought: meta.whyBought ?? null,
    why_detail: meta.whyDetail ?? null,
    product_id: meta.productId ?? null,
    order_count: meta.orderCount ?? null,
    is_before_second_shipment: meta.isBeforeSecondShipment ?? null,
    offer_declined: meta.offerDeclined ?? null,
    conversation_id: meta.conversationId ?? null,
  };
}

export async function submitCancelRequest(
  payload: { subscriptionId: number } & CancellationReasonPayload,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${payload.subscriptionId}/cancel-request?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, buildCancellationBody(payload));
}

/**
 * Remove ONE line from a multi-line subscription, leaving the others shipping.
 *
 * Calls the backend partial-cancellation route, which resolves the Stay AI line id
 * from the contract and invokes remove-lines immediately. Unlike swap, there is
 * no pending row or banner — the line is removed as soon as the request succeeds.
 */
export async function submitPartialCancelRequest(
  payload: {
    subscriptionId: number;
    /** The line to remove. Rides to the backend as `product_id`. */
    productId: number;
    /** Display title for success copy and cancel-meta. */
    productTitle: string;
    /** Titles of the lines that MUST keep shipping — the whole point. */
    retainedProductTitles: string[];
    customerEmail?: string | null;
    customerName?: string | null;
  } & CancellationReasonPayload,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${payload.subscriptionId}/partial-cancel-request?${buildQueryParams(identifier, discountCode)}`;
  await backendPostEmptyResponse(path, {
    ...buildCancellationBody(payload),
    product_title: payload.productTitle,
    retained_product_titles: payload.retainedProductTitles,
    ...(payload.customerEmail ? { customer_email: payload.customerEmail } : {}),
    ...(payload.customerName ? { customer_name: payload.customerName } : {}),
  });
}

export interface SwitchToOneTimeResult {
  invoice_url: string | null;
  amount?: number;
  currency?: string;
  already_existed?: boolean;
  cancelled?: boolean;
}

export async function submitSwitchToOneTime(
  payload: { subscriptionId: number } & CancellationReasonPayload,
): Promise<SwitchToOneTimeResult> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const path = `${PROXY_PREFIX}/subscriptions/${payload.subscriptionId}/switch-to-one-time?${buildQueryParams(identifier)}`;
  return backendFetch<SwitchToOneTimeResult>(path, {
    method: "POST",
    body: JSON.stringify(buildCancellationBody(payload)),
  });
}

export interface ScheduleCancelAfterNextDeliveryResult {
  scheduled: boolean;
  cancels_after: string | null;
}

/**
 * Schedule the cancellation to take effect right after the next delivery
 * (instead of an immediate cancel + fee). `override` mirrors the
 * ?so_cancel_after_delivery test override; the Next proxy strips it.
 */
export async function submitScheduleCancelAfterNextDelivery(
  payload: {
    subscriptionId: number;
    feeAmountWaived: number | null;
    override: boolean;
  } & CancellationReasonPayload,
): Promise<ScheduleCancelAfterNextDeliveryResult> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const path = `${PROXY_PREFIX}/subscriptions/${payload.subscriptionId}/schedule-cancel-after-next-delivery?${buildQueryParams(identifier)}`;
  return backendFetch<ScheduleCancelAfterNextDeliveryResult>(path, {
    method: "POST",
    body: JSON.stringify({
      ...buildCancellationBody(payload),
      fee_amount_waived: payload.feeAmountWaived,
      override: payload.override,
    }),
  });
}

/** Undo a scheduled cancel ("Keep my subscription"). */
export async function submitUndoScheduledCancel(payload: {
  subscriptionId: number;
}): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const path = `${PROXY_PREFIX}/subscriptions/${payload.subscriptionId}/undo-scheduled-cancel?${buildQueryParams(identifier)}`;
  await backendFetch<unknown>(path, { method: "POST", body: JSON.stringify({}) });
}

export async function submitSwitchToFlexible(
  payload: { subscriptionId: number } & CancellationReasonPayload,
): Promise<void> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const path = `${PROXY_PREFIX}/subscriptions/${payload.subscriptionId}/switch-to-flexible?${buildQueryParams(identifier)}`;
  await backendPostEmptyResponse(path, buildCancellationBody(payload));
}

export interface SwapOfferProduct {
  id: number;
  title: string;
  image_url: string | null;
  /** Per-product price when the catalog exposes it; otherwise null. */
  price: number | null;
  /** The variant the price belongs to — the exact fulfillment instruction. */
  variantId: number | null;
  variantTitle: string | null;
}

export interface SwapOfferConfigEcho {
  minRatio: number;
  maxRatio: number;
  minItems: number;
  maxItems: number;
}

export interface SwapOfferResult {
  /** The deterministic default bundle (minItems..maxItems products, real prices). */
  products: SwapOfferProduct[];
  /** REAL combined price of the default bundle. */
  comboTotal: number | null;
  currentPrice: number | null;
  /** The menu the AI may compose a bundle from (empty ⇒ skip the swap lever). */
  candidates: SwapOfferProduct[];
  /** The value rules echoed by the backend, for validating AI compositions. */
  config: SwapOfferConfigEcho | null;
}

const EMPTY_SWAP_OFFER: SwapOfferResult = {
  products: [],
  comboTotal: null,
  currentPrice: null,
  candidates: [],
  config: null,
};

/**
 * Candidates + default bundle for the cancellation "product swap" offer. The
 * backend enforces the value rules (combined price within the similar-price
 * band around current delivery total, item bounds from effective line count,
 * website-available and never-bought products only) and returns an empty
 * result when no valid bundle exists, so the offer is skipped.
 *
 * `currentPrice` must be the delivery total being replaced (unit × quantity
 * for the scoped line(s)). Candidate prices are unit prices; applied swaps
 * always fulfill new lines at qty 1.
 */
export async function fetchSwapOffer(args: {
  subscriptionId: number;
  currentProductId: number;
  currentPrice: number | null;
  /** Effective line count (1 for partial cancel; contract length otherwise). */
  lineCount: number;
}): Promise<SwapOfferResult> {
  const identifier = getCustomerIdentifier();
  if (!identifier) return EMPTY_SWAP_OFFER;
  if (args.lineCount >= 3) return EMPTY_SWAP_OFFER;
  const discountCode = getDiscountCode();
  let qs = buildQueryParams(identifier, discountCode);
  qs += `&current_product_id=${encodeURIComponent(args.currentProductId)}`;
  qs += `&line_count=${encodeURIComponent(args.lineCount)}`;
  if (args.currentPrice != null) {
    qs += `&current_price=${encodeURIComponent(args.currentPrice)}`;
  }
  const url = `${API_BASE}${PROXY_PREFIX}/subscriptions/${args.subscriptionId}/swap-offer?${qs}`;
  const res = await fetch(url, { method: "GET", credentials: "omit" });
  if (!res.ok) return EMPTY_SWAP_OFFER;
  const body = await res.json().catch(() => null);
  const data = (body?.data ?? body ?? {}) as Record<string, unknown>;
  const parseProducts = (raw: unknown): SwapOfferProduct[] =>
    (Array.isArray(raw) ? raw : [])
      .map((p: Record<string, unknown>) => ({
        id: Number(p.id),
        title: typeof p.title === "string" ? p.title : "Product",
        image_url: typeof p.image_url === "string" ? p.image_url : null,
        price: typeof p.price === "number" ? p.price : null,
        variantId: typeof p.variant_id === "number" ? p.variant_id : null,
        variantTitle:
          typeof p.variant_title === "string" ? p.variant_title : null,
      }))
      .filter((p: SwapOfferProduct) => Number.isFinite(p.id));
  const rawConfig = (data.config ?? null) as Record<string, unknown> | null;
  const config: SwapOfferConfigEcho | null =
    rawConfig &&
    typeof rawConfig.min_ratio === "number" &&
    typeof rawConfig.max_ratio === "number" &&
    typeof rawConfig.min_items === "number" &&
    typeof rawConfig.max_items === "number"
      ? {
          minRatio: rawConfig.min_ratio,
          maxRatio: rawConfig.max_ratio,
          minItems: rawConfig.min_items,
          maxItems: rawConfig.max_items,
        }
      : null;
  return {
    products: parseProducts(data.products),
    comboTotal:
      typeof data.combo_total === "number" ? data.combo_total : null,
    currentPrice:
      typeof data.current_price === "number" ? data.current_price : null,
    candidates: parseProducts(data.candidates),
    config,
  };
}

export function swapOfferQueryOptions(args: {
  subscriptionId: number;
  currentProductId: number;
  currentPrice: number | null;
  lineCount: number;
}) {
  return {
    queryKey: [
      "swap-offer",
      args.subscriptionId,
      args.currentProductId,
      args.currentPrice,
      args.lineCount,
    ] as const,
    queryFn: () => fetchSwapOffer(args),
    retry: false as const,
    staleTime: 60_000,
  };
}

export interface SwapRequestResult {
  /**
   * True when the backend applied the swap right away; false when the request
   * went to support to complete manually (PENDING swap + email).
   */
  completed: boolean;
}

/**
 * Record that the customer accepted the cancellation product-swap offer.
 * Depending on the backend's automation setting this either applies the swap
 * immediately (`completed: true`) or inserts a PENDING swap and emails support
 * to finish manually (`completed: false`). `items` carries which VARIANT each
 * product's price belongs to, so fulfillment is exact (a deal priced on the
 * travel size is never silently fulfilled with the full size).
 */
export async function submitSwapRequest(
  subscriptionId: number,
  products: {
    id: number;
    title?: string | null;
    variantId?: number | null;
    variantTitle?: string | null;
    price?: number | null;
  }[],
  opts: {
    sourceProductId: number;
    conversationId?: string | null;
    sourceProductTitle?: string | null;
    comboTotal?: number | null;
    currentPrice?: number | null;
    cancelReasonLabel?: string | null;
    whyBought?: string | null;
    whyDetail?: string | null;
    customerEmail?: string | null;
    customerName?: string | null;
  },
): Promise<SwapRequestResult> {
  const identifier = getCustomerIdentifier();
  if (!identifier) throw new Error(NO_IDENTIFIER_MSG);
  const discountCode = getDiscountCode();
  const path = `${PROXY_PREFIX}/subscriptions/${subscriptionId}/swap-request?${buildQueryParams(identifier, discountCode)}`;
  const result = await backendFetch<{
    success?: boolean;
    completed?: boolean;
    message?: string;
  }>(path, {
    method: "POST",
    body: JSON.stringify({
      product_ids: products.map((p) => p.id),
      items: products.map((p) => ({
        product_id: p.id,
        ...(p.title ? { title: p.title } : {}),
        variant_id: p.variantId ?? null,
        variant_title: p.variantTitle ?? null,
        price: p.price ?? null,
      })),
      source_product_id: opts.sourceProductId,
      conversation_id: opts.conversationId ?? null,
      ...(opts.sourceProductTitle
        ? { source_product_title: opts.sourceProductTitle }
        : {}),
      ...(opts.comboTotal != null ? { combo_total: opts.comboTotal } : {}),
      ...(opts.currentPrice != null ? { current_price: opts.currentPrice } : {}),
      ...(opts.cancelReasonLabel
        ? { cancel_reason_label: opts.cancelReasonLabel }
        : {}),
      ...(opts.whyBought ? { why_bought: opts.whyBought } : {}),
      ...(opts.whyDetail ? { why_detail: opts.whyDetail } : {}),
      ...(opts.customerEmail ? { customer_email: opts.customerEmail } : {}),
      ...(opts.customerName ? { customer_name: opts.customerName } : {}),
    }),
  });
  return { completed: result?.completed === true };
}

/** A PENDING swap blocks cancel; CONFIRMED/REJECTED (and null) do not. */
export type SwapStatus = "PENDING" | "CONFIRMED" | "REJECTED" | null;

export interface SwapStatusProduct {
  id: number;
  title: string;
  imageUrl: string | null;
}

export interface SwapStatusResult {
  status: SwapStatus;
  /** Requested target products, hydrated server-side with title + image. */
  products: SwapStatusProduct[];
}

const EMPTY_SWAP_STATUS: SwapStatusResult = { status: null, products: [] };

/**
 * Current product-swap status for a subscription. Returns the latest swap's
 * status alongside the hydrated requested products, so the pending banner can
 * render product names/images without a second catalog lookup.
 */
export async function fetchSwapStatus(
  subscriptionId: number,
): Promise<SwapStatusResult> {
  const identifier = getCustomerIdentifier();
  if (!identifier) return EMPTY_SWAP_STATUS;
  const discountCode = getDiscountCode();
  const qs = buildQueryParams(identifier, discountCode);
  const url = `${API_BASE}${PROXY_PREFIX}/subscriptions/${subscriptionId}/swap?${qs}`;
  const res = await fetch(url, { method: "GET", credentials: "omit" });
  if (!res.ok) return EMPTY_SWAP_STATUS;
  const body = await res.json().catch(() => null);
  const data = (body?.data ?? body ?? {}) as Record<string, unknown>;
  const rawStatus = data.status;
  const status: SwapStatus =
    rawStatus === "PENDING" || rawStatus === "CONFIRMED" || rawStatus === "REJECTED"
      ? rawStatus
      : null;
  const rawProducts = Array.isArray(data.products) ? data.products : [];
  const products: SwapStatusProduct[] = rawProducts
    .map((p: Record<string, unknown>) => ({
      id: Number(p.id),
      title: typeof p.title === "string" ? p.title : "Product",
      imageUrl: typeof p.image_url === "string" ? p.image_url : null,
    }))
    .filter((p: SwapStatusProduct) => Number.isFinite(p.id));
  return { status, products };
}

export function swapStatusQueryKey(subscriptionId: number) {
  return ["swap-status", subscriptionId] as const;
}

export function swapStatusQueryOptions(subscriptionId: number) {
  return {
    queryKey: swapStatusQueryKey(subscriptionId),
    queryFn: () => fetchSwapStatus(subscriptionId),
    retry: false as const,
    staleTime: 60_000,
  };
}

export interface CancelChatConfig {
  enabled: boolean;
  /** 0-100: share of customers bucketed into the chat flow. */
  percentage: number;
  /** Runtime switch for "cancel after my next delivery" (absent ⇒ false). */
  cancelAfterDeliveryEnabled: boolean;
}

/**
 * Runtime rollout config for the cancellation chatbot (chat vs. legacy dialog),
 * controlled by CANCEL_CHAT_ROLLOUT_PERCENT on the Next.js backend. Failures
 * THROW rather than resolving to percentage 0: an unreachable config (network
 * error, ad-blocker, 5xx) means the assignment is UNKNOWN — the customer gets
 * the dialog and events stamp "unassigned", instead of being silently counted
 * into the real "dialog" arm and skewing the A/B (the fallback-to-control
 * lesson from the reviews experiment).
 */
export async function fetchCancelChatConfig(): Promise<CancelChatConfig> {
  const res = await fetch(`${API_BASE}${PROXY_PREFIX}/cancel-chat-config`, {
    method: "GET",
    credentials: "omit",
  });
  if (!res.ok) throw new Error(`cancel-chat-config unavailable (${res.status})`);
  const body = (await res.json().catch(() => null)) as {
    percentage?: unknown;
    cancelAfterDeliveryEnabled?: unknown;
  } | null;
  if (typeof body?.percentage !== "number" || !Number.isFinite(body.percentage)) {
    throw new Error("cancel-chat-config malformed");
  }
  const pct = Math.min(100, Math.max(0, Math.trunc(body.percentage)));
  return {
    enabled: pct > 0,
    percentage: pct,
    cancelAfterDeliveryEnabled: body.cancelAfterDeliveryEnabled === true,
  };
}

export function cancelChatConfigQueryOptions() {
  return {
    queryKey: ["cancel-chat-config"] as const,
    queryFn: fetchCancelChatConfig,
    retry: 1,
    staleTime: 5 * 60_000,
  };
}

/**
 * Is there a customer record behind this email?
 *
 * Called before sending a sign-in code so an unknown address is turned away at
 * the email step, rather than being handed a token that resolves to an empty
 * product feed with no way back out.
 *
 * Fails open, matching the proxy: a network error answers `true`, leaving the
 * customer with the ordinary OTP flow instead of a false "no account" wall.
 */
export async function checkCustomerExists(email: string): Promise<boolean> {
  try {
    const { exists } = await backendFetch<{ exists: boolean }>(
      `${PROXY_PREFIX}/auth/check-email`,
      { method: "POST", body: JSON.stringify({ email }) },
    );
    return exists !== false;
  } catch {
    return true;
  }
}

export async function sendSignInOtp(email: string): Promise<void> {
  await backendFetch<{ success: boolean }>(`${PROXY_PREFIX}/auth/send-otp`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function verifySignInOtp(
  email: string,
  otp: string,
): Promise<string> {
  const { token } = await backendFetch<{ token: string }>(
    `${PROXY_PREFIX}/auth/verify-otp`,
    { method: "POST", body: JSON.stringify({ email, otp }) },
  );
  storeToken(token);
  storeEmail(email);
  return token;
}

/** A product the buyer bought on this shop and hasn't rated yet. */
export interface RateableProduct {
  /** Catalog id — what ratings are submitted against. */
  id: number;
  title: string;
  imageUrl: string | null;
}

/**
 * Products the buyer can still rate on this shop — the same list the rate
 * email's sheet shows, scoped to the storefront the widget runs on. Keyed on
 * the Shopify customer id from the purchased-products response, which carries
 * it even in buy-again-token mode.
 */
export async function fetchProductsToRate({
  shop,
  shopifyCustomerId,
}: {
  shop: string;
  shopifyCustomerId: number | string;
}): Promise<RateableProduct[]> {
  const url = new URL(`${API_BASE}${PROXY_PREFIX}/products-to-rate`);
  url.searchParams.set("shop", shop);
  url.searchParams.set("shopify_customer_id", String(shopifyCustomerId));

  const res = await fetch(url.toString(), {
    method: "GET",
    credentials: "omit",
  });
  // The prompt is a bonus on top of a completed action — a failed lookup just
  // means no rating section, never an error the buyer sees.
  if (!res.ok) return [];

  const body = await res.json().catch(() => null);
  const products: unknown = (body?.data ?? body)?.products;
  if (!Array.isArray(products)) return [];

  return products
    .filter(
      (p): p is { id: number; title?: string; image_url?: string | null } =>
        p != null && typeof p === "object" && typeof p.id === "number",
    )
    .map((p) => ({
      id: p.id,
      title: p.title ?? "",
      imageUrl: p.image_url ?? null,
    }));
}

/**
 * Submit the buyer's picks — several products at once, each with an optional
 * review note (`comment`) or, after a 4–5★ rating, a life-better answer
 * (`life_better`). Returns what the server stored and the points it awarded.
 */
export async function submitProductRatings({
  shop,
  shopifyCustomerId,
  responses,
}: {
  shop: string;
  shopifyCustomerId: number | string;
  responses: WidgetRatingResponse[];
}): Promise<{ ratedCount: number; points: number }> {
  const url = new URL(`${API_BASE}${PROXY_PREFIX}/products-to-rate`);

  const res = await fetch(url.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "omit",
    body: JSON.stringify({
      shop,
      shopify_customer_id: String(shopifyCustomerId),
      responses: responses.map((r) => ({
        product_id: r.productId,
        rating: r.rating,
        ...(r.comment ? { comment: r.comment } : {}),
        ...(r.lifeBetter ? { life_better: r.lifeBetter } : {}),
      })),
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to submit ratings (${res.status})`);
  }

  const body = await res.json().catch(() => null);
  const data = body?.data ?? body ?? {};
  return {
    ratedCount: Number(data.rated_count ?? 0),
    points: Number(data.points ?? 0),
  };
}


/** The gift a buyer can redeem points for, offered after a rating. */
export interface RewardsOffer {
  /** Main local product id — what Social Ocean's /products?gift= takes. */
  productId: number;
  title: string;
  imageUrl: string | null;
}

/** What the thank-you can offer after a rating. */
export interface RewardsOfferResult {
  /** The first gift (kept for the one-gift state). */
  offer: RewardsOffer | null;
  /** Every giftable product the buyer just rated, in the given order (max 3). */
  offers: RewardsOffer[];
  /** How many gifts the buyer's points cover now. */
  giftsAvailable: number;
  /** Points still needed for a gift, when one isn't affordable; else null. */
  pointsToNext: number | null;
}

export const NO_REWARDS_OFFER: RewardsOfferResult = {
  offer: null,
  offers: [],
  giftsAvailable: 0,
  pointsToNext: null,
};

function parseRewardsOfferItem(raw: unknown): RewardsOffer | null {
  if (
    raw == null ||
    typeof raw !== "object" ||
    typeof (raw as { product_id?: unknown }).product_id !== "number"
  ) {
    return null;
  }
  const o = raw as { product_id: number; title?: unknown; image_url?: unknown };
  return {
    productId: o.product_id,
    title: typeof o.title === "string" ? o.title : "",
    imageUrl: typeof o.image_url === "string" && o.image_url ? o.image_url : null,
  };
}

/** Tolerant: the older `{ offer }`-only response still reads as one gift. */
export function parseRewardsOfferResponse(body: unknown): RewardsOfferResult {
  const data: unknown =
    body && typeof body === "object" && "data" in body
      ? (body as { data: unknown }).data
      : body;
  if (data == null || typeof data !== "object") return NO_REWARDS_OFFER;
  const d = data as Record<string, unknown>;

  const offer = parseRewardsOfferItem(d.offer);
  const parsed = Array.isArray(d.offers)
    ? d.offers
        .map(parseRewardsOfferItem)
        .filter((o): o is RewardsOffer => o !== null)
    : [];
  const offers = parsed.length > 0 ? parsed : offer ? [offer] : [];

  const rawGifts = Number(d.gifts_available);
  const giftsAvailable =
    Number.isFinite(rawGifts) && rawGifts >= 0
      ? Math.floor(rawGifts)
      : offers.length > 0
        ? 1
        : 0;

  const rawNext = d.points_to_next;
  const pointsToNext =
    typeof rawNext === "number" && Number.isFinite(rawNext) && rawNext > 0
      ? Math.ceil(rawNext)
      : null;

  return { offer: offer ?? offers[0] ?? null, offers, giftsAvailable, pointsToNext };
}

/**
 * The gifts to offer right after the buyer rated `productIds`, how many their
 * points cover and how far the next one is. Never conditioned on the rating
 * itself. Never throws: any failure just means no offer.
 */
export async function fetchRewardsOffer({
  shop,
  shopifyCustomerId,
  productIds,
}: {
  shop: string;
  shopifyCustomerId: number | string;
  productIds: number[];
}): Promise<RewardsOfferResult> {
  if (productIds.length === 0) return NO_REWARDS_OFFER;
  try {
    const url = new URL(`${API_BASE}${PROXY_PREFIX}/rewards-offer`);
    url.searchParams.set("shop", shop);
    url.searchParams.set("shopify_customer_id", String(shopifyCustomerId));
    for (const id of productIds) {
      url.searchParams.append("product_ids[]", String(id));
    }

    const res = await fetch(url.toString(), {
      method: "GET",
      credentials: "omit",
    });
    if (!res.ok) return NO_REWARDS_OFFER;

    const body = await res.json().catch(() => null);
    return parseRewardsOfferResponse(body);
  } catch {
    return NO_REWARDS_OFFER;
  }
}
